import type {
  Biometric,
  BiometricResult,
  BiometricReferenceRange,
  Question,
  QuestionAlternative,
  QuestionNumericRule,
  QuestionRespons,
} from "@/lib/api_client";

// ── Biometric lookup ────────────────────────────────────────

interface BiometricRef {
  nlk: string;
  name: string;
}

/** Find biometrics by NLK code, falling back to name matching */
function findBiometrics(
  biometrics: Biometric[],
  refs: BiometricRef[],
): Biometric[] {
  const nlkCodes = refs.map((r) => r.nlk).filter(Boolean);
  const byNlk = biometrics.filter((b) => b.nlk && nlkCodes.includes(b.nlk));
  if (byNlk.length > 0) return byNlk;
  return biometrics.filter((b) =>
    refs.some((r) => b.name.toLowerCase().includes(r.name.toLowerCase())),
  );
}

/**
 * Dr.Dropin BHT private panel — the fixed marker set used for the privat
 * partition of every drDropinBht visit. Hardcoded, versioned in git; not
 * editable per-patient and not derived from defaults / survey / engine
 * suggestions.
 *
 * Two deliberate variant choices vs. DEFAULT_BIOMARKER_REFS:
 *   - hs-CRP (NOR05091 / Fürst 453) instead of regular CRP — high-sensitivity
 *     reports actual numeric values down to ~0.1 mg/L; regular CRP is censored
 *     at <5 which breaks PhenoAge.
 *   - Non-fasting glucose (NPU02192 / Fürst 341) instead of fasting — BHT users
 *     aren't reliably fasting at workplace draws. Accepts the small upward bias
 *     in PhenoAge in exchange for never missing the marker.
 */
const DRDROPIN_BHT_PRIVATE_PANEL_REFS: BiometricRef[] = [
  { nlk: "NOR05172", name: "Hemoglobin" },
  { nlk: "NPU02593", name: "Leukocytter m/diff" },
  { nlk: "NOR05091", name: "hs-CRP" },
  { nlk: "NPU19763", name: "Ferritin" },
  { nlk: "NPU19651", name: "ALAT" },
  { nlk: "NPU04998", name: "Kreatinin" },
  { nlk: "NPU01567", name: "Kolesterol HDL" },
  { nlk: "NPU01568", name: "Kolesterol LDL" },
  { nlk: "NPU27300", name: "HbA1c" },
  { nlk: "NPU27547", name: "TSH" },
  { nlk: "NPU27783", name: "ALP" },
  { nlk: "NPU01944", name: "MCV" },
  { nlk: "NPU19673", name: "Albumin" },
  { nlk: "NPU02192", name: "Glukose ikke-fastende" },
  { nlk: "NPU18162", name: "RDW" },
];

/**
 * Kickstart private panel — the fixed marker set a Kickstart member gets on top
 * of whatever HELFO covers. Same deal as the Dr.Dropin BHT panel above: applied
 * automatically to every `kickstart` visit, not editable per-patient, and not
 * derived from defaults / survey / engine suggestions.
 *
 * Shares the 15 BHT markers (including the same hs-CRP and non-fasting glucose
 * variant choices) and adds two nutrient markers on top: active B12 and folate.
 * Listed out in full rather than spread from DRDROPIN_BHT_PRIVATE_PANEL_REFS so
 * the two panels can keep diverging without either one moving by accident.
 */
const KICKSTART_PANEL_REFS: BiometricRef[] = [
  { nlk: "NOR05172", name: "Hemoglobin" },
  { nlk: "NPU02593", name: "Leukocytter m/diff" },
  { nlk: "NOR05091", name: "hs-CRP" },
  { nlk: "NPU19763", name: "Ferritin" },
  { nlk: "NPU19651", name: "ALAT" },
  { nlk: "NPU04998", name: "Kreatinin" },
  { nlk: "NPU01567", name: "Kolesterol HDL" },
  { nlk: "NPU01568", name: "Kolesterol LDL" },
  { nlk: "NPU27300", name: "HbA1c" },
  { nlk: "NPU27547", name: "TSH" },
  { nlk: "NPU27783", name: "ALP" },
  { nlk: "NPU01944", name: "MCV" },
  { nlk: "NPU19673", name: "Albumin" },
  { nlk: "NPU02192", name: "Glukose ikke-fastende" },
  { nlk: "NPU18162", name: "RDW" },
  { nlk: "NPU27125", name: "Aktivt B12" },
  { nlk: "NPU02070", name: "Vitamin B9" },
];

// Lp(a) is only included in defaults when not tested in the last 10 years.
const LPA_REF: BiometricRef = { nlk: "NPU19840", name: "Lp(a)" };

// Default biomarkers for every health assessment
const DEFAULT_BIOMARKER_REFS: BiometricRef[] = [
  { nlk: "NOR25909", name: "eGFR" },
  { nlk: "NPU19763", name: "Ferritin" },
  { nlk: "NOR05172", name: "Hemoglobin" },
  { nlk: "NPU01567", name: "Kolesterol HDL" },
  { nlk: "NPU01568", name: "Kolesterol LDL" },
  { nlk: "NPU01566", name: "Kolesterol total" },
  { nlk: "NPU19695", name: "Apo A1" },
  { nlk: "NPU22299", name: "Apo B" },
  { nlk: "NPU10238", name: "Apob/ai ratio" },
  { nlk: "NPU27300", name: "HbA1c" },
  { nlk: "NPU27125", name: "Aktivt B12" },
  { nlk: "NPU02070", name: "B9" },
  { nlk: "NPU10267", name: "Vitamin D" },
  { nlk: "NPU19673", name: "albumin_gl" },
  { nlk: "NPU04998", name: "creatinine_mol_l" },
  { nlk: "NPU22069", name: "glucose_mmol_l" },
  { nlk: "NPU02647", name: "magnesium" },
  { nlk: "NPU19651", name: "Alat" },
  { nlk: "NOR05091", name: "c_reac_protein_mg_l" },
  { nlk: "NPU02636", name: "lymphs_value" },
  { nlk: "NPU02593", name: "leukocytes" },
  { nlk: "NPU01944", name: "mcv" },
  { nlk: "NPU18162", name: "rdw" },
  { nlk: "NPU27783", name: "alk_phos" },
];

// BMI-based biomarker suggestions
const BMI_REFS = {
  underweight: [
    { nlk: "NPU19673", name: "Albumin" },
    { nlk: "NPU10267", name: "Vitamin D" },
    { nlk: "NPU19763", name: "Ferritin" },
    { nlk: "NPU01700", name: "Vitamin B12" },
    { nlk: "NPU02070", name: "Vitamin B9/Folat" },
  ],
  moderateOverweight: [
    { nlk: "NPU01566", name: "Kolesterol total" },
    { nlk: "NPU01567", name: "Kolesterol HDL" },
    { nlk: "NPU01568", name: "Kolesterol LDL" },
    { nlk: "NPU22069", name: "Glukose, fastende" },
    { nlk: "NPU27300", name: "HbA1c" },
  ],
  highOverweight: [
    { nlk: "NPU22069", name: "Glukose, fastende" },
    { nlk: "NPU27300", name: "HbA1c" },
    { nlk: "NPU18005", name: "C-Peptid" },
    { nlk: "NPU01566", name: "Kolesterol total" },
    { nlk: "NPU01567", name: "Kolesterol HDL" },
    { nlk: "NPU01568", name: "Kolesterol LDL" },
    { nlk: "NPU19651", name: "ALAT" },
    { nlk: "NPU19654", name: "ASAT" },
    { nlk: "NPU58634", name: "FIB-4" },
  ],
} as const;

// ── Fürst analysis grouping ─────────────────────────────────

/**
 * Markers that share a Fürst analysis code (`furstId`), keyed by every member
 * of the group. One code is one order: requisitioning it returns every marker
 * behind it, so a group can only be ordered or skipped as a whole — picking a
 * single member is not something Fürst can honour, and the price is charged
 * once for the group either way.
 *
 * Only groups with more than one member are included; callers treat a missing
 * key as "this marker stands alone".
 */
export function buildFurstIdGroups(
  biometrics: Biometric[],
): Map<string, Set<string>> {
  const byFurstId = new Map<string, string[]>();
  for (const b of biometrics) {
    if (!b.furstId) continue;
    const existing = byFurstId.get(b.furstId);
    if (existing) existing.push(b.id);
    else byFurstId.set(b.furstId, [b.id]);
  }
  const groups = new Map<string, Set<string>>();
  for (const ids of byFurstId.values()) {
    if (ids.length < 2) continue;
    const group = new Set(ids);
    for (const id of ids) groups.set(id, group);
  }
  return groups;
}

/**
 * `ids` widened to every marker that comes back with them — each id's Fürst
 * group folded in, ids without a group left as themselves. `groups` is a map
 * from `buildFurstIdGroups`.
 */
export function expandToFurstGroups(
  ids: Iterable<string>,
  groups: Map<string, Set<string>>,
): Set<string> {
  const out = new Set(ids);
  for (const id of [...out]) {
    const group = groups.get(id);
    if (group) for (const sibling of group) out.add(sibling);
  }
  return out;
}

// ── Reference range lookup ───────────────────────────────────

/** Look up reference ranges for a biometric, filtered by gender and age. */
export function getRangesForBiometric(
  biometricId: string,
  gender: string | null | undefined,
  age: number | null,
  ranges: BiometricReferenceRange[],
): { optMin?: string; optMax?: string; normMin?: string; normMax?: string } {
  const matching = ranges.filter((r) => {
    if (r.biometricId !== biometricId) return false;
    if (r.gender && gender && r.gender !== gender) return false;
    if (age != null) {
      if (Number(r.minAge) > age || Number(r.maxAge) < age) return false;
    }
    return true;
  });
  return {
    optMin: matching.find((r) => r.referenceType === "optimal_min")?.value,
    optMax: matching.find((r) => r.referenceType === "optimal_max")?.value,
    normMin: matching.find((r) => r.referenceType === "normal_min")?.value,
    normMax: matching.find((r) => r.referenceType === "normal_max")?.value,
  };
}

// ── Lab visit sort ──────────────────────────────────────────

/**
 * Sort lab visits newest-first by clinical date (visitDate), falling back
 * to createdAt when visitDate is missing.
 *
 * Sorting by createdAt alone breaks down for users with bulk-imported
 * historical data (e.g. Fürst import dropping 5 years of visits in the
 * same second) — the "most recent" visit by createdAt is then whichever
 * row was inserted last, not the most clinically recent one. The
 * partition function below depends on the caller providing
 * clinically-correct newest-first order.
 */
export function sortLabVisitsByEffectiveDate<
  T extends { visitDate: string | null; createdAt: string },
>(visits: T[]): T[] {
  return [...visits].sort(
    (a, b) => effectiveTime(b) - effectiveTime(a),
  );
}

function effectiveTime(v: {
  visitDate: string | null;
  createdAt: string;
}): number {
  if (v.visitDate && v.visitDate.length > 0) {
    const t = new Date(v.visitDate).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return new Date(v.createdAt).getTime();
}

// ── Source: Outside range ───────────────────────────────────

/**
 * For each biometric measured in a previous visit, determine whether
 * the MOST RECENT result was outside range. Returns two sets:
 *   - currentlyOutside: most recent measurement is outside reference range
 *   - previouslyOutside: was outside at some earlier point but the most
 *     recent measurement is back inside range
 *
 * sortedVisits is expected newest-first — use sortLabVisitsByEffectiveDate
 * to produce it. Biometrics with no usable reference range or no numeric
 * values are skipped entirely.
 *
 * If `sendableBiometricIds` is provided, biometrics not in that set are also
 * skipped — used to filter out historical results for markers we no longer
 * test (e.g. retired catalog entries with no furstId). Without this guard
 * such markers can land in the HELFO partition and crash the Fürst send
 * with an empty-after-filter requisition. Such filtered markers, when they
 * would otherwise have been outside-range, are returned in `skippedHistorical`
 * so the UI can surface them to the doctor.
 */

export type SkippedHistoricalReason = "not_sendable";

export type SkippedHistoricalEntry = {
  biometricId: string;
  reason: SkippedHistoricalReason;
};

export function partitionOutsideRangeBioIds(params: {
  currentVisitId: string;
  sortedVisits: { id: string }[];
  bioResults: BiometricResult[];
  referenceRanges: BiometricReferenceRange[];
  gender?: string | null;
  age?: number | null;
  sendableBiometricIds?: Set<string>;
}): {
  currentlyOutside: Set<string>;
  previouslyOutside: Set<string>;
  skippedHistorical: SkippedHistoricalEntry[];
} {
  const {
    currentVisitId,
    sortedVisits,
    bioResults,
    referenceRanges,
    gender,
    age,
    sendableBiometricIds,
  } = params;

  const previousVisitOrder = new Map<string, number>();
  let idx = 0;
  for (const v of sortedVisits) {
    if (v.id === currentVisitId) continue;
    previousVisitOrder.set(v.id, idx++);
  }

  type Eval = { outside: boolean; visitOrder: number };
  const byBiometric = new Map<string, Eval[]>();
  // Track biometrics that have at least one out-of-range historical reading
  // but were filtered out by sendableBiometricIds. We surface these in the UI
  // so the doctor knows we noticed but couldn't follow up.
  const skippedNotSendable = new Set<string>();

  for (const result of bioResults) {
    const visitOrder = previousVisitOrder.get(result.labVisitId);
    if (visitOrder === undefined) continue;

    const ranges = getRangesForBiometric(
      result.biometricId,
      gender,
      age ?? null,
      referenceRanges,
    );
    if (!ranges.normMin && !ranges.normMax) continue;

    const value = parseFloat(result.value);
    if (isNaN(value)) continue;

    const belowMin =
      ranges.normMin !== undefined && value < parseFloat(ranges.normMin);
    const aboveMax =
      ranges.normMax !== undefined && value > parseFloat(ranges.normMax);
    const outside = belowMin || aboveMax;

    const notSendable =
      sendableBiometricIds !== undefined &&
      !sendableBiometricIds.has(result.biometricId);

    if (notSendable) {
      if (outside) skippedNotSendable.add(result.biometricId);
      continue;
    }

    const list = byBiometric.get(result.biometricId) ?? [];
    list.push({ outside, visitOrder });
    byBiometric.set(result.biometricId, list);
  }

  const currentlyOutside = new Set<string>();
  const previouslyOutside = new Set<string>();

  for (const [biometricId, evals] of byBiometric) {
    evals.sort((a, b) => a.visitOrder - b.visitOrder);
    const mostRecent = evals[0];
    if (!mostRecent) continue;
    if (mostRecent.outside) {
      currentlyOutside.add(biometricId);
    } else if (evals.some((e) => e.outside)) {
      previouslyOutside.add(biometricId);
    }
  }

  const skippedHistorical: SkippedHistoricalEntry[] = [...skippedNotSendable].map(
    (biometricId) => ({ biometricId, reason: "not_sendable" }),
  );

  return { currentlyOutside, previouslyOutside, skippedHistorical };
}

// ── HELFO rules v2 ───────────────────────────────────────────

/** Kap. 3: a previously abnormal marker is a HELFO control only this long after it was taken. */
export const HELFO_V2_CONTROL_INTERVAL_MONTHS = 12;

/** When each biometric's latest numeric result in an earlier visit was taken. */
export function getLatestResultDates(params: {
  currentVisitId: string;
  sortedVisits: { id: string; visitDate: string | null; createdAt: string }[];
  bioResults: BiometricResult[];
}): Map<string, Date> {
  const { currentVisitId, sortedVisits, bioResults } = params;
  const visitTime = new Map(
    sortedVisits
      .filter((v) => v.id !== currentVisitId)
      .map((v) => [v.id, effectiveTime(v)]),
  );
  const latest = new Map<string, number>();
  for (const r of bioResults) {
    const t = visitTime.get(r.labVisitId);
    if (t === undefined || Number.isNaN(parseFloat(r.value))) continue;
    if (t > (latest.get(r.biometricId) ?? -Infinity)) latest.set(r.biometricId, t);
  }
  return new Map([...latest].map(([id, t]) => [id, new Date(t)]));
}

/**
 * Rules v2 on the two HELFO paths, as the backend's `replayHelfo` replays
 * them: a known condition keeps its markers off both, a whole Fürst code at
 * a time (a blocked Kreatinin takes eGFR with it), and a previously abnormal
 * marker is a control only once it is 12 months old.
 */
export function applyHelfoRulesV2(params: {
  engine: Set<string>;
  outside: Set<string>;
  blocked: Set<string>;
  lastTaken: Map<string, Date>;
  now: Date;
  furstIdGroups: Map<string, Set<string>>;
}): { engine: Set<string>; outside: Set<string> } {
  const { engine, outside, lastTaken, now, furstIdGroups } = params;
  const blocked = expandToFurstGroups(params.blocked, furstIdGroups);
  const months = (from: Date) =>
    (now.getFullYear() - from.getFullYear()) * 12 +
    (now.getMonth() - from.getMonth());
  return {
    engine: new Set([...engine].filter((id) => !blocked.has(id))),
    outside: new Set(
      [...outside].filter((id) => {
        const taken = lastTaken.get(id);
        return (
          !blocked.has(id) &&
          taken !== undefined &&
          months(taken) >= HELFO_V2_CONTROL_INTERVAL_MONTHS
        );
      }),
    ),
  };
}

// ── Source: Previously measured markers ─────────────────────

/**
 * Every biometric the patient has a result for in an earlier visit —
 * regardless of whether it was in or outside range. Lets the doctor
 * re-order "the same as last time" in one click.
 *
 * Self-reported visits are excluded: those values were typed in by the
 * patient from an external printout, so they say nothing about what Amino
 * can requisition. `sendableBiometricIds`, when provided, drops markers we
 * can no longer order from Fürst (retired catalog entries), same guard as
 * partitionOutsideRangeBioIds.
 */
export function getPreviouslyMeasuredBioIds(params: {
  currentVisitId: string;
  sortedVisits: { id: string; selfReported?: boolean | null }[];
  bioResults: BiometricResult[];
  sendableBiometricIds?: Set<string>;
}): Set<string> {
  const { currentVisitId, sortedVisits, bioResults, sendableBiometricIds } =
    params;

  const eligibleVisitIds = new Set<string>();
  for (const v of sortedVisits) {
    if (v.id === currentVisitId) continue;
    if (v.selfReported) continue;
    eligibleVisitIds.add(v.id);
  }

  const ids = new Set<string>();
  for (const r of bioResults) {
    if (!eligibleVisitIds.has(r.labVisitId)) continue;
    if (sendableBiometricIds && !sendableBiometricIds.has(r.biometricId))
      continue;
    ids.add(r.biometricId);
  }
  return ids;
}

// ── Source: Survey answers ───────────────────────────────────

/** Returns the default biomarker IDs that every health assessment should include. */
export function getDefaultBioIds(biometrics: Biometric[]): Set<string> {
  return new Set(
    findBiometrics(biometrics, DEFAULT_BIOMARKER_REFS).map((b) => b.id),
  );
}

/**
 * Returns the Lp(a) biometric ID if the user has not been tested for Lp(a) in
 * the last 10 years (or never). Returns null if tested within that window.
 *
 * The caller handles HELFO priority — if the engine assigns Lp(a) to HELFO,
 * the normal partition logic removes it from the privat set automatically.
 */
export function getLpaConditionalBioId(params: {
  biometrics: Biometric[];
  bioResults: BiometricResult[];
  sortedVisits: { id: string; visitDate: string | null; createdAt: string }[];
  currentVisitId: string;
}): string | null {
  const { biometrics, bioResults, sortedVisits, currentVisitId } = params;

  const lpaBios = findBiometrics(biometrics, [LPA_REF]);
  if (lpaBios.length === 0) return null;
  const lpaId = lpaBios[0]!.id;

  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 10);
  const cutoffMs = cutoff.getTime();

  const visitDateMap = new Map<string, number>();
  for (const visit of sortedVisits) {
    if (visit.id === currentVisitId) continue;
    visitDateMap.set(visit.id, effectiveTime(visit));
  }

  const testedRecently = bioResults.some(
    (r) =>
      r.biometricId === lpaId &&
      (visitDateMap.get(r.labVisitId) ?? 0) >= cutoffMs,
  );

  return testedRecently ? null : lpaId;
}

/**
 * Returns the biomarker IDs in the fixed Dr.Dropin BHT private panel.
 * Used to seed the privat partition for `drDropinBht` lab visits — the panel
 * itself is not editable per-patient.
 */
export function getDrDropinBhtPrivatePanelBioIds(
  biometrics: Biometric[],
): Set<string> {
  return new Set(
    findBiometrics(biometrics, DRDROPIN_BHT_PRIVATE_PANEL_REFS).map((b) => b.id),
  );
}

/**
 * Returns the biomarker IDs in the fixed Kickstart private panel.
 * Used to seed the privat partition for `kickstart` lab visits — the panel
 * itself is not editable per-patient.
 */
export function getKickstartPanelBioIds(
  biometrics: Biometric[],
): Set<string> {
  return new Set(
    findBiometrics(biometrics, KICKSTART_PANEL_REFS).map((b) => b.id),
  );
}

/**
 * Marker swaps applied to the HELFO partition of a fixed-panel visit, keyed
 * `from` → `to`. Both fixed panels deliberately order non-fasting variants:
 * a BHT draw happens at the workplace and a Kickstart draw at whatever slot
 * the member booked, so nobody is reliably fasting. A HELFO requisition for
 * a fasting marker on those patients comes back uninterpretable (truly so
 * for triglycerides, where a meal can raise the value several-fold) or
 * forces a second visit — and ordering both variants just bills Fürst twice
 * for the same question. So when the HELFO side wants the fasting variant,
 * the non-fasting one goes on the HELFO requisition instead and the fasting
 * one is dropped.
 *
 * Ordinary visits are unaffected — the doctor tells those patients to fast.
 */
const FIXED_PANEL_HELFO_SUBSTITUTION_REFS: {
  from: BiometricRef;
  to: BiometricRef;
}[] = [
  {
    from: { nlk: "NPU22069", name: "Glukose, fastende" },
    to: { nlk: "NPU02192", name: "Glukose ikke-fastende" },
  },
  {
    from: { nlk: "NPU03620", name: "Triglyserider (fastende)" },
    to: { nlk: "NPU04094", name: "Triglyserider (ikke-fastende)" },
  },
];

/**
 * Resolves FIXED_PANEL_HELFO_SUBSTITUTION_REFS against the catalog into
 * `fromBiometricId` → `toBiometricId`. A pair whose replacement isn't in the
 * catalog is skipped entirely, leaving the original marker in place — a
 * missing swap target must never silently drop a marker HELFO asked for.
 */
export function getFixedPanelHelfoSubstitutions(
  biometrics: Biometric[],
): Map<string, string> {
  const subs = new Map<string, string>();
  for (const { from, to } of FIXED_PANEL_HELFO_SUBSTITUTION_REFS) {
    const toBio = findBiometrics(biometrics, [to])[0];
    if (!toBio) continue;
    for (const fromBio of findBiometrics(biometrics, [from])) {
      if (fromBio.id !== toBio.id) subs.set(fromBio.id, toBio.id);
    }
  }
  return subs;
}

/**
 * Rewrites every substituted marker in `ids` to its replacement, collapsing
 * onto the replacement if both variants are present. Returns `ids` itself
 * when nothing matched, so callers can keep it in a `useMemo`/effect
 * dependency without triggering a re-sync on every render.
 */
export function applyBioIdSubstitutions(
  ids: Set<string>,
  substitutions: ReadonlyMap<string, string>,
): Set<string> {
  if (substitutions.size === 0) return ids;
  let changed = false;
  const out = new Set<string>();
  for (const id of ids) {
    const to = substitutions.get(id);
    if (to === undefined) {
      out.add(id);
    } else {
      out.add(to);
      changed = true;
    }
  }
  return changed ? out : ids;
}

/**
 * Which substitutions are actually in force right now, as `from` → `to` pairs.
 *
 * Wanting a swap is not the same as having one. The HELFO partition on a visit
 * that already has a requisition is whatever the DB holds, not what the engine
 * would derive today — a draft created before a substitution rule existed, or
 * one the doctor edited, can still carry the original marker. Reporting the
 * swap off the engine's inputs alone would then explain something that never
 * happened, so a pair counts only when the replacement is on the HELFO side
 * and the marker it replaced is not.
 */
export function resolveAppliedBioIdSubstitutions(params: {
  substitutions: ReadonlyMap<string, string>;
  /** Marker IDs the HELFO side asked for, before substitution. */
  requested: Set<string>;
  /** The HELFO set actually in effect — DB state, or the engine preview. */
  effectiveHelfo: Set<string>;
}): { fromBiometricId: string; toBiometricId: string }[] {
  const { substitutions, requested, effectiveHelfo } = params;
  const applied: { fromBiometricId: string; toBiometricId: string }[] = [];
  for (const [fromBiometricId, toBiometricId] of substitutions) {
    if (!requested.has(fromBiometricId)) continue;
    if (!effectiveHelfo.has(toBiometricId)) continue;
    if (effectiveHelfo.has(fromBiometricId)) continue;
    applied.push({ fromBiometricId, toBiometricId });
  }
  return applied;
}

/**
 * The privat partition a fixed-panel visit (Dr.Dropin BHT, Kickstart) should
 * hold. The panel is a floor, not a ceiling:
 *
 *   - every panel marker is present, so the set can't drift off the panel;
 *   - markers the doctor added on top are carried over, so re-applying the
 *     panel doesn't wipe them;
 *   - anything that has moved into the HELFO requisition is stripped, since
 *     the two partitions must never overlap.
 *
 * Removing a *panel* marker is deliberately not expressible — the only way one
 * leaves the privat partition is by being pulled into HELFO.
 */
export function resolveFixedPanelPrivatBioIds(params: {
  panel: Set<string>;
  currentPrivat: Set<string>;
  helfo: Set<string>;
}): Set<string> {
  const { panel, currentPrivat, helfo } = params;
  const result = new Set<string>();
  for (const id of panel) if (!helfo.has(id)) result.add(id);
  for (const id of currentPrivat) if (!helfo.has(id)) result.add(id);
  return result;
}

/** Returns biomarker IDs triggered by the user's survey answers (alternatives, numeric rules, and BMI). */
export function getSurveyBioIds(params: {
  biometrics: Biometric[];
  questions: Question[];
  questionAlternatives: QuestionAlternative[];
  questionNumericRules: QuestionNumericRule[];
  questionResponses: QuestionRespons[];
}): Set<string> {
  const {
    biometrics,
    questions,
    questionAlternatives,
    questionNumericRules,
    questionResponses,
  } = params;

  const validBioIds = new Set(biometrics.map((b) => b.id));
  const ids = new Set<string>();

  // 1. BMI-based suggestions
  const bmi = calculateBMI(questionResponses);
  if (bmi !== null) {
    const refs =
      bmi < 18.5
        ? BMI_REFS.underweight
        : bmi >= 30
          ? BMI_REFS.highOverweight
          : bmi >= 24
            ? BMI_REFS.moderateOverweight
            : [];
    for (const bio of findBiometrics(biometrics, [...refs])) {
      ids.add(bio.id);
    }
  }

  // 2. Suggestions from question alternatives and numeric rules
  for (const response of questionResponses) {
    const question = questions.find((q) => q.id === response.questionId);
    if (!question) continue;

    // Check alternatives (multiple-choice answers)
    const alternatives = questionAlternatives.filter(
      (a) => a.questionId === question.id,
    );
    if (alternatives.length > 0) {
      const selectedTexts = response.response.split(";").map((r) => r.trim());
      for (const alt of alternatives) {
        if (selectedTexts.includes(alt.text)) {
          const recIds = alt.recommendedBiometricIds as string[] | null;
          recIds?.forEach((id) => {
            if (validBioIds.has(id)) ids.add(id);
          });
        }
      }
    }

    // Check numeric rules
    if (question.isNumber) {
      const rules = questionNumericRules.filter(
        (r) => r.questionId === question.id,
      );
      const value = parseFloat(response.response);
      if (!isNaN(value)) {
        for (const rule of rules) {
          const threshold = parseFloat(rule.treshold);
          const matches =
            (rule.arithmetic === "equal" && value === threshold) ||
            (rule.arithmetic === "greater_than" && value > threshold) ||
            (rule.arithmetic === "less_than" && value < threshold);
          if (matches) {
            const recIds = rule.recommendedBiometricIds as string[] | null;
            recIds?.forEach((id) => {
              if (validBioIds.has(id)) ids.add(id);
            });
          }
        }
      }
    }
  }

  return ids;
}

// ── BMI calculation ─────────────────────────────────────────

function calculateBMI(responses: QuestionRespons[]): number | null {
  let height: number | null = null;
  let weight: number | null = null;

  for (const r of responses) {
    if (r.questionText?.toLowerCase().includes("høyde")) {
      const v = parseFloat(r.response || "");
      if (!isNaN(v)) height = v;
    }
    if (r.questionText?.toLowerCase().includes("vekt")) {
      const v = parseFloat(r.response || "");
      if (!isNaN(v)) weight = v;
    }
  }

  if (!height || !weight || height <= 0) return null;
  const m = height / 100;
  return parseFloat((weight / (m * m)).toFixed(1));
}

// ── Comment generation ──────────────────────────────────────

const HEALTH_AREA_CHECKS: { area: string; patterns: string[] }[] = [
  { area: "metabolsk helse", patterns: ["glukose", "c-peptid", "hba1c"] },
  { area: "nyrefunksjon", patterns: ["kreatinin", "egfr"] },
  { area: "leverfunksjon", patterns: ["alat", "asat", "gt"] },
  { area: "betennelsesmarkører", patterns: ["crp", "sr"] },
  {
    area: "hjerte- og karsystem",
    patterns: ["kolesterol", "ldl", "hdl", "apob", "apoa1", "triglyserid"],
  },
  {
    area: "hormonstatus",
    patterns: ["testosteron", "østradiol", "progesteron"],
  },
  { area: "stoffskifte", patterns: ["tsh", "t4", "t3"] },
  {
    area: "næringsstatus",
    patterns: [
      "b12",
      "folat",
      "b9",
      "vitamin d",
      "magnesium",
      "ferritin",
      "jern",
    ],
  },
];
